import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { SUPABASE_CONFIG } from '@/config/dataSource';

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Verificar se as configurações do Supabase estão corretas
if (!SUPABASE_CONFIG.URL || !SUPABASE_CONFIG.ANON_KEY) {
  console.error('❌ Configurações do Supabase não encontradas!');
  console.error('URL:', SUPABASE_CONFIG.URL);
  console.error('ANON_KEY:', SUPABASE_CONFIG.ANON_KEY ? 'Definido' : 'Não definido');
}

export const supabase = createClient<Database>(
  SUPABASE_CONFIG.URL,
  SUPABASE_CONFIG.ANON_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      debug: false, // Desabilitar debug em produção
      storageKey: 'multimarcas-auth-token'
    },
    realtime: {
      params: {
        eventsPerSecond: 2, // Reduzido para ser mais estável
        heartbeatIntervalMs: 60000, // Heartbeat a cada 60 segundos
        reconnectAfterMs: 2000, // Reconectar após 2 segundos
        maxRetries: 5 // Máximo de 5 tentativas
      }
    },
    global: {
      headers: {
        'X-Client-Info': 'multimarcas-frontend',
      }
    },
    db: {
      schema: 'public'
    }
  }
);

// Cache simples para evitar consultas desnecessárias
const profileCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Função para obter perfil com cache
export const getCachedProfile = async (userId: string) => {
  const cached = profileCache.get(userId);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    console.log('[Supabase] Usando perfil do cache');
    return cached.data;
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        tenant:tenants(
          id,
          nome
        )
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;

    // Armazenar no cache
    profileCache.set(userId, { data: { profile: data, error: null }, timestamp: now });

    return { profile: data, error: null };
  } catch (error) {
    console.error('[Supabase] Erro ao buscar perfil:', error);
    return { profile: null, error };
  }
};

// Função para limpar cache
export const clearProfileCache = (userId?: string) => {
  if (userId) {
    profileCache.delete(userId);
  } else {
    profileCache.clear();
  }
};

// Sistema de monitoramento de conectividade (apenas logs)
let isOnline = navigator.onLine;

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    console.log('[Supabase] Conectividade restaurada');
    clearProfileCache();
  });

  window.addEventListener('offline', () => {
    isOnline = false;
  });
}

// Função para verificar se está online
export const isSupabaseOnline = () => isOnline;

// Função para limpar cache local
export const clearLocalCache = () => {
  try {
    // Limpar localStorage
    localStorage.clear();

    // Limpar sessionStorage
    sessionStorage.clear();

    // Limpar cache do navegador
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    console.log('Cache limpo com sucesso');
  } catch (error) {
    console.error('Erro ao limpar cache:', error);
  }
};

// Função para resetar sessão
export const resetSession = async () => {
  try {
    // Fazer logout
    await supabase.auth.signOut();

    // Limpar cache
    clearLocalCache();

    // Recarregar página
    window.location.reload();
  } catch (error) {
    console.error('Erro ao resetar sessão:', error);
  }
};

// Função para verificar saúde da conexão
export const checkConnectionHealth = async () => {
  try {
    const start = Date.now();
    const { error } = await supabase
      .from('clientes')
      .select('id')
      .limit(1);

    const responseTime = Date.now() - start;

    return {
      healthy: !error && responseTime < 5000,
      responseTime,
      error: error?.message
    };
  } catch (error) {
    return {
      healthy: false,
      responseTime: null,
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
};

// Teste de conectividade básica
supabase.auth.getSession().then(({ error }) => {
  if (error) {
    console.warn('⚠️ Aviso: Erro ao conectar com Supabase:', error.message);
    console.warn('💡 Dica: Verifique se o domínio local está configurado no painel do Supabase');
  } else {
  }
}).catch((error) => {
  console.error('❌ Erro crítico ao configurar Supabase:', error);
});